import { type FC, useCallback, useEffect, useRef } from 'react';

import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';

import { tr } from '../../constants';
import type { LinkCardOptions } from './LinkCard';

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

export const LinkCardView: FC<NodeViewProps> = ({ node, editor, extension, updateAttributes, deleteNode }) => {
  const { translations } = extension.options as LinkCardOptions;
  const url = str(node.attrs.url);
  const title = str(node.attrs.title);
  const description = str(node.attrs.description);
  const image = str(node.attrs.image);
  const siteName = str(node.attrs.siteName);
  const loading = node.attrs.loading === true;

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!loading || fetchedRef.current) return;
    fetchedRef.current = true;

    const onFetch = (extension.options as LinkCardOptions).onFetchLinkPreview;
    if (!onFetch || !url) {
      updateAttributes({ loading: false });
      return;
    }

    let cancelled = false;
    onFetch(url)
      .then((meta) => {
        if (cancelled) return;
        updateAttributes({
          title: meta?.title ?? null,
          description: meta?.description ?? null,
          image: meta?.image ?? null,
          siteName: meta?.siteName ?? null,
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) updateAttributes({ loading: false });
      });

    return () => {
      cancelled = true;
      // Without the reset, a StrictMode remount (or dep change) cancels the only
      // fetch and the early-return above strands the card on loading forever.
      fetchedRef.current = false;
    };
  }, [loading, url, extension, updateAttributes]);

  const handleDelete = useCallback(() => {
    deleteNode();
  }, [deleteNode]);

  const { hostname, faviconSrc } = (() => {
    try {
      const parsed = new URL(url);
      return { hostname: parsed.hostname, faviconSrc: `${parsed.origin}/favicon.ico` };
    } catch {
      return { hostname: url, faviconSrc: '' };
    }
  })();

  return (
    <NodeViewWrapper as="div" className="community-editor-link-card" contentEditable={false}>
      <div className="relative my-2 flex overflow-hidden rounded-xl border border-black/10 bg-white">
        {loading ? (
          <div className="flex w-full items-center gap-3 p-3 text-sm text-black/50">
            <span className="size-4 animate-spin rounded-full border-2 border-black/20 border-t-black/60" />
            <span>{tr(translations, 'community.composer.link_preview_loading')}</span>
          </div>
        ) : (
          <>
            {image && (
              <div className="w-[88px] min-h-[88px] shrink-0 overflow-hidden bg-black/5">
                <img
                  src={image}
                  alt=""
                  className="size-full object-cover"
                  draggable={false}
                  referrerPolicy="no-referrer"
                />
              </div>
            )}
            <div className="flex min-w-0 flex-col justify-center gap-0.5 p-3">
              <span className="flex min-w-0 items-center gap-1.5 text-xs text-black/50">
                {!image && faviconSrc && (
                  <img
                    src={faviconSrc}
                    alt=""
                    className="size-4 shrink-0 rounded-sm"
                    draggable={false}
                    referrerPolicy="no-referrer"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <span className="truncate">{siteName || hostname}</span>
              </span>
              {title && <span className="truncate text-sm font-semibold text-black/90">{title}</span>}
              {description && <span className="line-clamp-2 text-xs text-black/60">{description}</span>}
            </div>
          </>
        )}

        {editor.isEditable && (
          <button
            type="button"
            onClick={handleDelete}
            aria-label={tr(translations, 'community.composer.link_preview_remove')}
            className="absolute right-1 top-1 inline-flex size-6 items-center justify-center rounded-full bg-black/70 text-white shadow-sm transition-colors hover:bg-black"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    </NodeViewWrapper>
  );
};
